/**
 * Responsables del cierre de mes: a quién se le cobra cada parte de una unidad
 * (gastos comunes y montos del propietario) según las tenencias del período.
 * Lógica pura extraída de CierreMes.jsx para poder testearla.
 *
 * Reglas:
 *   - Solo cuenta el lugar titular (slot "primary"). El complementario
 *     (ownerSecondary/tenantSecondary) no responde por GC ni por fondos; sus
 *     tenencias quedan para el histórico de acceso.
 *   - Con un único tramo responsable en el período, si esa tenencia está
 *     cerrada con fecha (dateTo) se le cobra a ESE userid y no a quien figura
 *     hoy en `apartments`: el cambio se suele cargar después del día efectivo
 *     y antes de correr el cierre. Si está abierta manda `apartments` (es la
 *     misma persona o la tenencia quedó desfasada por una edición sin fechas).
 *   - Sin tenencias (datos legacy) se usa la foto de `apartments`.
 */
import { normalizeCutoffDay } from "./cierrePeriod";
import {
  daysOverlapInMonth,
  distributeByDayWeights,
  minusOneDay,
  monthBounds,
  resolveDailyResponsibility,
  splitByDays,
} from "./occupancies";

const tieneValor = (v) => v != null && v !== "" && String(v).trim() !== "";

/** Titular legacy del GC: inquilino vigente en apartments; si no, propietario; si no, unit_<id>. */
export const legacyTenantHolder = (apt) => {
  const hasTenant = apt.tenant != null && apt.tenant !== "" && String(apt.tenant).trim() !== "";
  return hasTenant ? String(apt.tenant) : (apt.owner ? String(apt.owner) : `unit_${apt.id}`);
};

/** Titular legacy de lo que paga el propietario: propietario vigente o unit_<id>. */
export const legacyOwnerHolder = (apt) => (apt.owner ? String(apt.owner) : `unit_${apt.id}`);

/**
 * userid del único tramo si terminó con fecha; null si hay cero o varios, si
 * está abierto (sin dateTo) o si le falta el userid. Una tenencia abierta de
 * alguien que ya no está en la unidad (desfasada: la unidad se editó sin
 * cerrarla) no debe seguir cobrándole; ahí decide la foto de apartments.
 */
export function responsableUnico(tramos) {
  if (!Array.isArray(tramos) || tramos.length !== 1) return null;
  const t = tramos[0] || {};
  if (!tieneValor(t.dateTo)) return null;
  return tieneValor(t.userid) ? String(t.userid).trim() : null;
}

/**
 * Tenencias del período agrupadas por unidad y rol: { [aptId]: { tenant, owner } }.
 * Ignora el lugar complementario (slot "secondary"); sin slot cuenta como titular.
 */
export function tenenciasPorUnidad(tenencias) {
  const porUnidad = {};
  for (const o of tenencias || []) {
    if (!o || (o.slot || "primary") !== "primary") continue;
    const k = String(o.apartmentId);
    if (!porUnidad[k]) porUnidad[k] = { tenant: [], owner: [] };
    if (o.role === "tenant") porUnidad[k].tenant.push(o);
    else if (o.role === "owner") porUnidad[k].owner.push(o);
  }
  return porUnidad;
}

/**
 * Ventana de la proración en la convención (start, end] de occupancies.js.
 * Con día de corte la ventana del cierre ya es (start, end]. Sin día de corte
 * la ventana por defecto es el mes calendario [día 1, último]: el inicio
 * exclusivo pasa al día anterior para que el día 1 cuente. Si el admin editó
 * el inicio (p. ej. "Usar" el fin del cierre anterior) ya es exclusivo.
 * @param {{month:number, year:number, periodStart:string, periodEnd:string, diaCorte?:any}} p
 * @returns {{start:string, end:string}}
 */
export function ventanaDeProracion({ month, year, periodStart, periodEnd, diaCorte = null }) {
  const inicio = periodStart ? String(periodStart).slice(0, 10) : "";
  if (!normalizeCutoffDay(diaCorte) && inicio && inicio === monthBounds(Number(month), Number(year)).start) {
    return { start: minusOneDay(inicio), end: periodEnd };
  }
  return { start: periodStart, end: periodEnd };
}

/**
 * Reparto de un monto que paga el OCUPANTE (clase GC): inquilino del día; si no
 * hay, propietario del día. `tramos` es la entrada de tenenciasPorUnidad.
 * @returns {{prorrateado:boolean, partes:Array<{userid:string, amount:number, role?:string, days?:number}>}}
 */
export function repartoGastoComun({ amount, apt, tramos, month, year, windowBounds = null }) {
  const delInquilino = (tramos && tramos.tenant) || [];
  const delPropietario = (tramos && tramos.owner) || [];
  // Semilla legacy: un rol sin tenencias en el período toma al ocupante de
  // apartments por todo el mes (datos previos a occupancies).
  const tenantTramos = delInquilino.length > 0
    ? delInquilino
    : (tieneValor(apt.tenant) ? [{ userid: apt.tenant, dateFrom: null, dateTo: null }] : []);
  const ownerTramos = delPropietario.length > 0
    ? delPropietario
    : (apt.owner ? [{ userid: apt.owner, dateFrom: null, dateTo: null }] : []);
  // Un único responsable que cubre todo el período → sin proración. Si su
  // tramo no cubre todo (inquilino que se fue sin reemplazo), se prorratea:
  // sus días a él, el resto al propietario.
  const dias = daysOverlapInMonth({ dateFrom: null, dateTo: null }, month, year, windowBounds);
  const cubreTodo = (tr) => tr.length === 1 && daysOverlapInMonth(tr[0], month, year, windowBounds) === dias;
  const sinProracion = tenantTramos.length <= 1 && ownerTramos.length <= 1 && (
    tenantTramos.length === 1
      ? cubreTodo(tenantTramos)
      : (ownerTramos.length === 0 || cubreTodo(ownerTramos))
  );
  if (sinProracion) {
    const unico = tenantTramos.length === 1 ? responsableUnico(tenantTramos) : responsableUnico(ownerTramos);
    return { prorrateado: false, partes: [{ userid: unico || legacyTenantHolder(apt), amount }] };
  }
  const shares = resolveDailyResponsibility({
    tenantTramos,
    ownerTramos,
    month,
    year,
    fallbackOwnerId: legacyOwnerHolder(apt),
    windowBounds,
  });
  if (shares.length <= 1) {
    return { prorrateado: false, partes: [{ userid: shares[0] ? shares[0].userid : legacyTenantHolder(apt), amount }] };
  }
  return { prorrateado: true, partes: distributeByDayWeights(amount, shares) };
}

/**
 * Reparto de un monto que paga SIEMPRE el propietario (criterio "owner", fondo
 * de reserva, fondos custom): por días entre los propietarios del período.
 * @returns {{prorrateado:boolean, partes:Array<{userid:string, amount:number, role?:string, days?:number}>}}
 */
export function repartoPropietario({ amount, apt, tramos, month, year, windowBounds = null }) {
  const ownerTramos = (tramos && tramos.owner) || [];
  if (ownerTramos.length >= 2) {
    const split = splitByDays(amount, ownerTramos, month, year, undefined, windowBounds);
    if (split.length >= 2) {
      return {
        prorrateado: true,
        partes: split.map((s) => ({ userid: String(s.userid), role: "owner", days: s.days, amount: s.amount })),
      };
    }
    return { prorrateado: false, partes: [{ userid: responsableUnico(split) || legacyOwnerHolder(apt), amount }] };
  }
  // Cero o un tramo: el de la tenencia cerrada del período o, si no, el propietario vigente.
  return { prorrateado: false, partes: [{ userid: responsableUnico(ownerTramos) || legacyOwnerHolder(apt), amount }] };
}
